import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Moon, Sun } from 'lucide-react'

export type Theme = 'dark' | 'light'
const THEME_STORAGE_KEY = 'aiflow-theme'

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch { /* localStorage may be unavailable */ }
  // Night mode is the product default. A user's explicit choice above still
  // takes precedence, but the operating system preference should not change
  // the initial experience when no choice has been saved yet.
  return 'dark'
}

interface ThemeContextValue { theme: Theme; toggleTheme: () => void }
const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({children}: {children: ReactNode}) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme)
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.documentElement.style.colorScheme = theme
    try { window.localStorage.setItem(THEME_STORAGE_KEY, theme) } catch { /* best effort */ }
  }, [theme])
  const value = useMemo(() => ({theme, toggleTheme: () => setTheme(current => current === 'dark' ? 'light' : 'dark')}), [theme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme must be used within ThemeProvider')
  return value
}

export function ThemeToggle({className = ''}: {className?: string}) {
  const {theme, toggleTheme} = useTheme()
  const light = theme === 'light'
  return <button type="button" className={`theme-toggle ${className}`.trim()} onClick={toggleTheme} aria-label={light ? '切换到暗色模式' : '切换到亮色模式'} title={light ? '切换到暗色模式' : '切换到亮色模式'}>
    <span className="theme-toggle-icon" aria-hidden="true">{light ? <Sun/> : <Moon/>}</span><span className="theme-toggle-label">{light ? '亮色' : '暗色'}</span>
  </button>
}
